import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Card, CardActionArea, CardContent, Container, Grid, Link, Typography } from '@mui/material';
import Hero from '../../sections/Hero';
import JunkRemovalInfo from '../../sections/JunkRemovalInfo';
import FaqAccordion, { faqSchema } from '../../sections/FaqAccordion';
import QuoteRequestForm from '../../sections/QuoteRequestForm';
import { junkRemovalCities } from '../junk-removal/junkRemovalCities';
import { PHONE_DISPLAY, PHONE_NUMBER, SITE_URL } from '../../../utils/site';

const faqs = [
  {
    question: 'How much does junk removal cost?',
    answer: `Junk removal starts at $100. The final price depends on how much space your items take up in our trailer, how heavy they are and what it costs to dispose of or recycle them. Call ${PHONE_DISPLAY} or send the form below for a free quote.`,
  },
  {
    question: 'What do you haul?',
    answer:
      'Furniture, appliances, most electronics, garage and basement clutter, yard waste, and construction and remodeling debris, for homes and businesses.',
  },
  {
    question: "What can't you take?",
    answer:
      "We can't take hazardous materials like chemicals, paint, solvents or other household hazardous waste. Brown, Outagamie and Door counties all have hazardous waste programs for those.",
  },
  {
    question: 'Do I have to carry anything out?',
    answer: "No. We do the lifting, whether it's in the basement, the garage or an upstairs bedroom.",
  },
  {
    question: 'Where does my stuff go?',
    answer:
      "We donate usable items to local charities when we can and sort out what can be recycled. Wisconsin law keeps electronics and major appliances out of landfills, so those get recycled. The rest is disposed of properly.",
  },
  {
    question: 'Do you work with businesses?',
    answer: 'Yes. We handle commercial junk removal too, like old office furniture and equipment.',
  },
];

const schema = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'Service',
      serviceType: 'Junk removal',
      name: 'Junk Removal',
      url: `${SITE_URL}/services/junk-removal`,
      provider: { '@type': 'MovingCompany', name: 'JBR Delivery LLC', telephone: PHONE_DISPLAY, url: SITE_URL },
      areaServed: ['Green Bay', 'De Pere', 'Appleton', 'Neenah', 'Oshkosh', 'Sturgeon Bay'].map((name) => ({
        '@type': 'City',
        name: `${name}, WI`,
      })),
      offers: {
        '@type': 'Offer',
        priceSpecification: { '@type': 'PriceSpecification', minPrice: 100, priceCurrency: 'USD' },
      },
    },
    faqSchema(faqs),
  ],
};

const JunkRemovalPage: React.FC = () => {
  return (
    <>
      <Helmet>
        <title>Junk Removal in Green Bay, Appleton & Door County</title>
        <meta
          name="description"
          content="Junk removal in Green Bay, the Fox Cities and Door County. We do the lifting, haul it away in our trailer, and donate or recycle what we can. From $100."
        />
        <script type="application/ld+json">{JSON.stringify(schema)}</script>
      </Helmet>

      <Hero
        title="Junk Removal in Green Bay & Northeast Wisconsin"
        subtitle="Furniture, appliances, garage and basement clutter, yard waste and more. We do the lifting, load it into our trailer and haul it away."
        backgroundImage="/assets/images/junk-removal-bg.jpg"
        defaultService="junk-removal"
      />

      <Box component="section" py={8}>
        <Container maxWidth="lg">
          <Typography variant="h2" component="h2" gutterBottom>
            Junk removal from a local crew
          </Typography>
          <Typography variant="body1" paragraph>
            JBR Delivery LLC hauls away the stuff you're done with, for homes and businesses across Green Bay,
            the Fox Cities, Oshkosh and Door County. We bring our trailer, carry everything out, load it up and
            haul it away, so you get your space back without renting a truck or making trips to the dump.
          </Typography>
          <Typography variant="body1" paragraph>
            Junk removal starts at $100. Call{' '}
            <Link href={`tel:${PHONE_NUMBER}`}>{PHONE_DISPLAY}</Link> or <Link href="#quote">send us a few details</Link>{' '}
            for a free quote.
          </Typography>
        </Container>
      </Box>

      <JunkRemovalInfo />

      <Box component="section" py={8}>
        <Container maxWidth="lg">
          <Typography variant="h2" component="h2" gutterBottom>
            Junk removal near you
          </Typography>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            {junkRemovalCities.map((city) => (
              <Grid item xs={12} md={4} key={city.slug}>
                <Card elevation={2} sx={{ height: '100%' }}>
                  <CardActionArea component={RouterLink} to={`/junk-removal/${city.slug}`} sx={{ height: '100%' }}>
                    <CardContent sx={{ p: 3 }}>
                      <Typography variant="h5" component="h3" gutterBottom>
                        {city.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {city.communities.slice(0, 5).join(', ')} and nearby
                      </Typography>
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>
            ))}
          </Grid>
          <Typography variant="body1" sx={{ mt: 4 }}>
            We also cover Oshkosh and the rest of Northeast Wisconsin. Not sure we come to you? Give us a call.
          </Typography>
        </Container>
      </Box>

      <Box component="section" py={8} sx={{ backgroundColor: 'grey.50' }}>
        <Container maxWidth="lg">
          <Typography variant="h2" component="h2" gutterBottom align="center" sx={{ mb: 6 }}>
            Junk removal questions
          </Typography>
          <FaqAccordion faqs={faqs} />
        </Container>
      </Box>

      <QuoteRequestForm defaultService="junk-removal" />
    </>
  );
};

export default JunkRemovalPage;
